'use client';

import { useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { motion } from 'framer-motion';
import { ISummerSale } from '@/sanity/lib/definitions';
import { DEFAULT_LANGUAGE, resolveI18nText } from '@/sanity/globals/I18n';
import { resolveLink } from '@/sanity/globals/CTA';
import bannerBg from '@/public/global/summer-sale/banner-bg.png';

interface SummerSaleTopBannerProps {
  data: ISummerSale;
  ctaUrl: string;
  language?: string;
}

// "Up to [[85% off]] Challenges & Funding + Raffle!" — [[ ]] becomes the serif green highlight.
function parseHighlightedText(text: string) {
  const parts = text.split(/\[\[(.+?)\]\]/g);
  return (
    <div className="flex flex-wrap items-center justify-center gap-x-[0.7rem] gap-y-[0.25rem]">
      {parts.map((part, i) => {
        if (!part.trim()) return null;
        if (i % 2 === 1) {
          return (
            <span key={i} className="relative inline-flex shrink-0 flex-col items-center">
              <span
                className="font-blacker italic text-[2.4rem] md:text-[3.4rem] xl:text-[4.9rem] leading-[1.12] tracking-[-0.08rem] xl:tracking-[-0.15rem] relative z-10"
                style={{ color: '#003828' }}
              >
                {part}
              </span>
              <span
                className="absolute bottom-[0.15em] left-0 block h-[0.35em] w-full"
                style={{ background: 'rgba(34,142,113,0.13)' }}
              />
            </span>
          );
        }
        return (
          <span
            key={i}
            className="font-geist font-medium text-[1.9rem] md:text-[2.75rem] xl:text-[4rem] leading-[1.06] tracking-[-0.08rem] xl:tracking-[-0.21rem] whitespace-nowrap text-black"
          >
            {part.trim()}
          </span>
        );
      })}
    </div>
  );
}

export default function SummerSaleTopBanner({
  data,
  ctaUrl,
  language,
}: SummerSaleTopBannerProps) {
  const endDate = useMemo(
    () => (data.endDate ? new Date(data.endDate) : null),
    [data.endDate],
  );
  const link = resolveLink(ctaUrl, language || DEFAULT_LANGUAGE);
  const [finalLink, setFinalLink] = useState("");
  const [expired, setExpired] = useState(false);

  useEffect(() => {
    if (!link) return;
    setFinalLink(link);
  }, [link]);

  useEffect(() => {
    if (!endDate) return;
    const check = () => setExpired(endDate.getTime() - Date.now() <= 0);
    check();
    const interval = setInterval(check, 1000);
    return () => clearInterval(interval);
  }, [endDate]);

  if (endDate && expired) return null;

  const headline = resolveI18nText(data.bannerText, data.bannerTextI18n, language);
  const ctaLabel = resolveI18nText(
    data.bannerCtaLabel,
    data.bannerCtaLabelI18n,
    language,
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 'calc(-100% - 1.5rem)' }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
      className="relative w-full overflow-hidden bg-white"
    >
      {/* Faint crypto texture */}
      <Image
        src={bannerBg}
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 z-0 !h-full !w-full object-cover opacity-[0.12] mix-blend-luminosity"
      />

      <div className="relative z-10 flex flex-wrap items-center justify-center gap-x-[1.5rem] gap-y-[1.25rem] px-[2rem] py-[1.5rem] text-center xl:flex-nowrap xl:gap-[2rem] xl:px-[4rem] xl:py-[1.75rem]">
        {/* Headline — full-width top row on mobile, centered middle column on desktop */}
        <div className="order-1 flex w-full justify-center xl:order-2 xl:w-auto xl:flex-1">
          {headline && parseHighlightedText(headline)}
        </div>

        {/* Wordmark — sits next to the CTA on mobile, far left on desktop */}
        <span
          className="order-2 shrink-0 font-bebas uppercase leading-[0.8] tracking-[-0.12rem] xl:order-1 xl:tracking-[-0.27rem] text-[2.75rem] md:text-[3.5rem] xl:text-[5.25rem]"
          style={{ color: '#ff7248' }}
        >
          Summer Sale
        </span>

        {/* CTA — next to the wordmark on mobile, far right on desktop */}
        {ctaLabel && (
          <Link
            href={finalLink ?? ctaUrl}
            className="order-3 shrink-0 flex items-center justify-center gap-[0.6rem] rounded-[0.25rem] px-[2rem] xl:px-[4rem] py-[1.25rem] xl:py-[1.9rem] font-geist font-semibold capitalize text-[1.5rem] md:text-[1.9rem] xl:text-[2.15rem] tracking-[-0.04rem] whitespace-nowrap transition-all hover:brightness-95"
            style={{ background: '#12f0b4', color: '#001b14' }}
          >
            {ctaLabel}
            <svg className="shrink-0 w-[1.4em] h-[1.4em]" viewBox="0 0 16 16" fill="#001b14" xmlns="http://www.w3.org/2000/svg">
              <path d="M10.7833 8.66667H2.66667V7.33333H10.7833L7.05 3.6L8 2.66667L13.3333 8L8 13.3333L7.05 12.4L10.7833 8.66667Z" />
            </svg>
          </Link>
        )}
      </div>
    </motion.div>
  );
}
